'use client';

import React from 'react';

interface BlogContentWrapperProps {
  compiledContent: React.ReactElement | null | undefined;
  fallbackContent?: string;
  slug: string;
}

/**
 * Bulletproof wrapper for blog content that handles all edge cases
 * during static generation and runtime
 */
export function BlogContentWrapper({ 
  compiledContent, 
  fallbackContent,
  slug 
}: BlogContentWrapperProps) {
  // DEFENSIVE: Handle all possible states
  if (!compiledContent) {
    console.warn(`[BlogContentWrapper] No compiled content for: ${slug}`);
    
    if (fallbackContent) {
      return (
        <div className="prose prose-lg max-w-none">
          <div dangerouslySetInnerHTML={{ __html: fallbackContent }} />
        </div>
      );
    }
    
    return (
      <div className="prose prose-lg max-w-none">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-yellow-900 mb-2">
            Content is being processed
          </h3>
          <p className="text-yellow-800">
            This content is currently being compiled. Please refresh the page in a few moments.
          </p>
        </div>
      </div>
    );
  }
  
  // SUCCESS: Render the compiled MDX content
  return (
    <div className="prose prose-lg max-w-none
      prose-headings:font-bold prose-headings:text-gray-900
      prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6 prose-h2:pb-3 prose-h2:border-b prose-h2:border-gray-200
      prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-4
      prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-6
      prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-a:font-medium
      prose-ul:list-disc prose-ul:pl-6 prose-ul:my-6
      prose-ol:list-decimal prose-ol:pl-6 prose-ol:my-6
      prose-li:text-gray-700 prose-li:mb-2
      prose-blockquote:border-l-4 prose-blockquote:border-primary prose-blockquote:pl-6 prose-blockquote:italic prose-blockquote:my-8
      prose-blockquote:bg-gray-50 prose-blockquote:py-4 prose-blockquote:pr-4 prose-blockquote:rounded-r-lg
      prose-code:bg-gray-100 prose-code:px-2 prose-code:py-1 prose-code:rounded prose-code:text-sm
      prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-pre:rounded-lg prose-pre:shadow-lg
      prose-img:rounded-lg prose-img:shadow-lg prose-img:my-8
      prose-table:shadow-sm prose-table:rounded-lg prose-table:overflow-hidden
      prose-th:bg-gray-50 prose-th:font-semibold
      prose-strong:text-gray-900 prose-strong:font-bold">
      {compiledContent}
    </div>
  );
}